import React, { useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, RotateCw } from "lucide-react";

/**
 * Opening Balances Wizard
 * ────────────────────────
 * 3-step flow:
 *   1. Download the seeded CoA template (already tagged with expected sign)
 *   2. Upload the filled CSV — preview lines, imbalance, & per-row action
 *   3. Confirm → server writes `opening_balance` on every matched account
 */
const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function OpeningBalancesWizard() {
  const [file, setFile]       = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [done, setDone]       = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/accounts/opening-balances/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "opening_balances_template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (e) { toast.error("Download failed"); }
  };

  const uploadPreview = async () => {
    if (!file) return toast.error("Pick a CSV file first");
    setBusy(true); setPreview(null); setDone(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/accounts/opening-balances/preview", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      if (!r.data.balanced)
        toast.warning(`Trial balance is out by ${inr(Math.abs(r.data.difference))}`);
      else
        toast.success("Preview ready — review then commit");
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const commit = async () => {
    if (!preview) return;
    if (!preview.balanced &&
        !window.confirm(`Trial balance is out by ${inr(Math.abs(preview.difference))}. Import anyway?`)) return;
    setBusy(true);
    try {
      // DB convention: opening_balance is SIGNED with Dr-positive.
      //   Dr entry  → positive
      //   Cr entry  → negative
      // (naturalOb() in Accounts.jsx renders the Dr/Cr side from this sign.)
      const rows = preview.rows.filter(r => r.resolved).map(r => ({
        account_code: r.account_code,
        opening_balance: (Number(r.debit) || 0) - (Number(r.credit) || 0),
      }));
      const r = await api.post("/accounts/opening-balances/import", { rows });
      setDone(r.data);
      toast.success(`Applied to ${r.data.applied} accounts · ${r.data.skipped} skipped`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="opening-balances-wizard">
      <div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Opening Balances</div>
        <div className="text-sm text-muted-foreground mt-1">
          Import your day-one trial balance from any Tally / Zoho / SAP export. Two-step: preview → commit.
        </div>
      </div>

      {/* Step 1 */}
      <Card className="p-4 rounded-sm border">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center font-mono text-xs font-bold">1</div>
            <div>
              <div className="font-semibold text-sm">Download the template</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">
                Pre-filled with your Chart of Accounts. Fill the <code>opening_debit</code> or <code>opening_credit</code> column — never both.
              </div>
            </div>
          </div>
          <Button onClick={downloadTemplate} variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="ob-download-template">
            <Download className="w-3.5 h-3.5" /> CoA CSV
          </Button>
        </div>
      </Card>

      {/* Step 2 */}
      <Card className="p-4 rounded-sm border">
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center font-mono text-xs font-bold">2</div>
          <div className="flex-1">
            <div className="font-semibold text-sm">Upload your filled CSV</div>
            <div className="text-[11px] text-muted-foreground mt-0.5 mb-3">
              We'll parse every row, match it to a CoA code, and show you the trial-balance total before anything is written.
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Input type="file" accept=".csv,text/csv"
                       onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); setDone(null); }}
                       className="rounded-sm max-w-sm h-9" data-testid="ob-file-input" />
              <Button onClick={uploadPreview} disabled={busy || !file} size="sm"
                        className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="ob-preview-btn">
                {busy ? <RotateCw className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Preview
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Preview + Step 3 */}
      {preview && (
        <Card className="rounded-sm border overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">// Preview</div>
              <div className="font-semibold text-sm mt-0.5">{preview.row_count} rows parsed · {preview.errors.length} errors</div>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Total Dr</div>
                <div className="font-mono font-semibold">{inr(preview.total_debit)}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
              <div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">Total Cr</div>
                <div className="font-mono font-semibold">{inr(preview.total_credit)}</div>
              </div>
              <div className={`px-3 py-1.5 rounded-sm text-xs font-medium ${preview.balanced ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}
                   data-testid="ob-tb-status">
                {preview.balanced ? (<><CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> Balanced</>) : (<><AlertTriangle className="w-3.5 h-3.5 inline mr-1" /> Out by {inr(Math.abs(preview.difference))}</>)}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-96">
            <table className="w-full text-xs">
              <thead className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground bg-muted/40 sticky top-0">
                <tr>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Code</th>
                  <th className="text-left p-2">Account</th>
                  <th className="text-right p-2">Debit</th>
                  <th className="text-right p-2">Credit</th>
                  <th className="text-left p-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map(r => (
                  <tr key={r.line} className={`border-t ${!r.resolved ? "bg-red-500/5" : ""}`} data-testid={`ob-row-${r.line}`}>
                    <td className="p-2 text-muted-foreground">{r.line}</td>
                    <td className="p-2 font-mono">{r.account_code}</td>
                    <td className="p-2">{r.account_name || <span className="text-red-500 italic">Unknown</span>}</td>
                    <td className="p-2 text-right font-mono">{r.debit ? inr(r.debit) : "—"}</td>
                    <td className="p-2 text-right font-mono">{r.credit ? inr(r.credit) : "—"}</td>
                    <td className="p-2 text-[10px] font-mono">{r.action || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-accent/15 text-accent flex items-center justify-center font-mono text-xs font-bold">3</div>
              <div>
                <div className="font-semibold text-sm">Commit to your books</div>
                <div className="text-[11px] text-muted-foreground">
                  Only the {preview.rows.filter(r => r.resolved).length} matched rows will be written.
                </div>
              </div>
            </div>
            <Button onClick={commit} disabled={busy || done !== null} size="sm"
                      className="rounded-sm bg-emerald-600 hover:bg-emerald-500 text-white gap-1.5" data-testid="ob-commit-btn">
              <Upload className="w-3.5 h-3.5" /> {done ? "Applied" : "Commit"}
            </Button>
          </div>

          {done && (
            <div className="p-3 border-t bg-emerald-500/5 text-sm text-emerald-700 flex items-center gap-2" data-testid="ob-done">
              <CheckCircle2 className="w-4 h-4" /> Applied to <b>{done.applied}</b> accounts. Skipped <b>{done.skipped}</b>.
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
